/**
 * FollowerPreviewTable
 * Live preview of current month's follower eligibility
 */

import type { FollowerPreview } from '@hooks/useCreatorEarnings';
import { INELIGIBILITY_LABELS } from '@parlay-picker/shared/creatorPayout';
import { formatCents } from '@parlay-picker/shared/formatting';
import { cn } from '@utils/cn';
import { AlertTriangle, Check, X } from 'lucide-react';
import type { ReactElement } from 'react';

interface FollowerPreviewTableProps {
  followerPreview: FollowerPreview;
  loading: boolean;
}

export function FollowerPreviewTable({
  followerPreview,
  loading,
}: FollowerPreviewTableProps): ReactElement {
  if (loading) {
    return (
      <div className="bg-theme-surface/50 rounded-lg border border-theme-border p-6">
        <div className="animate-pulse space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 bg-theme-border rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (followerPreview.followers.length === 0) {
    return (
      <div className="bg-theme-surface/50 rounded-lg border border-theme-border p-6 text-center">
        <p className="text-sm text-theme-text-secondary">No followers yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-theme-surface/50 rounded-lg overflow-hidden border border-theme-border">
      <div className="px-4 py-3 border-b border-theme-border">
        <h3 className="text-sm font-semibold text-theme-text">Current Month Preview</h3>
        <p className="text-xs text-theme-text-tertiary mt-0.5">
          Live preview of next payout calculation ({followerPreview.eligibleCount} of{' '}
          {followerPreview.totalCount} eligible)
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-theme-surface border-b border-theme-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Follower
              </th>
              <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Days
              </th>
              <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Sportsbook
              </th>
              <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Wagered
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-theme-border">
            {followerPreview.followers.map((follower) => (
              <tr key={follower.followerId}>
                <td className="px-4 py-3 text-sm font-medium text-theme-text">
                  {follower.displayName}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-sm text-theme-text-secondary text-right">
                  {follower.followDurationDays}d
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-center">
                  {follower.hasSportsbookLinked ? (
                    <Check className="w-4 h-4 text-theme-success inline" />
                  ) : (
                    <X className="w-4 h-4 text-theme-danger inline" />
                  )}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-sm text-theme-text-secondary text-right">
                  {formatCents(follower.wageredCents)}
                </td>
                <td className="px-4 py-3 text-right">
                  {follower.isEligible ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-theme-success">
                      <Check className="w-3.5 h-3.5" />
                      Eligible
                    </span>
                  ) : (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 text-xs font-medium text-theme-warning',
                      )}
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {INELIGIBILITY_LABELS[
                        follower.ineligibilityReason as keyof typeof INELIGIBILITY_LABELS
                      ] ?? 'Ineligible'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
